import { produce } from 'immer';
import { createSelector } from 'reselect';
import { uniqBy } from 'lodash-es';
import { ScopedType, ScopedAsyncType } from './scoped-type';
import { asyncAction } from './async-action';
import { actions as fieldActions } from './fields-dux';
import { actions as alertActions } from './alerts-dux';
import { getCurrentOperations } from '../../lib/api/growers';

const SLICE = 'growerOperations';
const Type = ScopedType('GROWER');
const AsyncType = ScopedAsyncType('GROWER');
const SELECT = Type('SELECT');
const SELECT_ORGANIZATION = Type('SELECT_ORGANIZATION');
const LOAD_OPERATIONS = AsyncType('LOAD_OPERATIONS');

export const selectors = {
  all: createSelector(
    (state) => state[SLICE]?.growers,
    (state) => state[SLICE]?.selectedOrganization,
    (growers, selectedOrganization) =>
      selectedOrganization?.id
        ? growers.filter(
            (grower) => grower.organizationId === selectedOrganization.id,
          )
        : growers,
  ),
  selected: (state) => state[SLICE]?.selectedGrowerOperation,
  selectedId: (state) => state[SLICE]?.selectedGrowerOperation?.guid,
  selectedName: (state) => state[SLICE]?.selectedGrowerOperation?.operationName,
  selectedOrganization: (state) => state[SLICE]?.selectedOrganization,
  organizations: createSelector(
    (state) => state[SLICE].growers,
    (growers) =>
      uniqBy(
        growers?.map((grower) => grower.organization),
        'id',
      ),
  ),
};

export const actions = {
  loadOperations: () =>
    asyncAction(LOAD_OPERATIONS, () => getCurrentOperations()),
  setSelectedGrowerOperation(grower) {
    return async (dispatch) => {
      dispatch(alertActions.refreshBanner(grower.guid));
      dispatch({ type: SELECT, payload: grower });
      dispatch(fieldActions.loadGrowerFields(grower.guid));
    };
  },
  resetSelectedGrowerOperation() {
    return { type: SELECT, payload: null };
  },
  setSelectedOrganization(organization) {
    return { type: SELECT_ORGANIZATION, payload: organization };
  },
};

const reducerHandlers = {
  [SELECT]: reduceSelect,
  [SELECT_ORGANIZATION]: reduceSelectOrganization,
  [LOAD_OPERATIONS.complete]: reduceLoadOperations,
};

function reduceSelect(state, payload) {
  state.selectedGrowerOperation = payload;
}

function reduceSelectOrganization(state, payload) {
  state.selectedOrganization = payload;
}

function reduceLoadOperations(state, payload) {
  state.growers = payload;
}

export const reducers = {
  [SLICE]: (
    state = { selectedGrowerOperation: null, growers: [] },
    { type, payload } = {},
  ) => {
    if (!reducerHandlers[type]) return state;
    return produce(state, (draft) => reducerHandlers[type](draft, payload));
  },
};
